import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { priceFor } from './priceFormat';

// Shopping cart shared across the app (product detail, navbar, cart/checkout).
// The cart holds AT MOST ONE item — adding a product replaces whatever was
// there, so the user never juggles conflicting seat/term combinations. The
// item carries the product's full price list so term and seat count can be
// adjusted from within the cart without another fetch:
//   { productId, productName, imageUrl, term, seatCount, prices }

const CART_STORAGE_KEY = 'nuvolem.cart';

const CartContext = createContext(null);

// A stored item is valid only if it has a product id and a price list to drive
// in-cart term/seat editing. Anything else (including the old array-shaped cart
// from a previous version) is discarded.
function isValidItem(value) {
  return (
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    value.productId != null &&
    Array.isArray(value.prices)
  );
}

function readStoredItem() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    return isValidItem(data) ? data : null;
  } catch {
    // localStorage may be unavailable (private mode / quota) — start empty.
    return null;
  }
}

export function CartProvider({ children }) {
  const [item, setItem] = useState(readStoredItem);

  // Persist on every change; best-effort, mirroring ProductsPage's cache writes.
  useEffect(() => {
    try {
      if (item) localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(item));
      else localStorage.removeItem(CART_STORAGE_KEY);
    } catch {
      // Ignore — persistence is a nicety, not a requirement.
    }
  }, [item]);

  const value = useMemo(() => {
    const matches = (productId, term, seatCount) =>
      !!item &&
      item.productId === productId &&
      item.term === term &&
      item.seatCount === seatCount;

    // Replace the cart with this item (single-item cart).
    const setCartItem = next => setItem(next);

    // Adjust the current item's term and/or seat count in place.
    const updateItem = changes =>
      setItem(prev => (prev ? { ...prev, ...changes } : prev));

    const clear = () => setItem(null);

    const itemCount = item ? 1 : 0;
    const unitPrice = item ? priceFor(item.prices, item.term, item.seatCount) : 0;

    return { item, matches, setCartItem, updateItem, clear, itemCount, unitPrice };
  }, [item]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within a CartProvider');
  return ctx;
}
